import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BsFilterRight } from "react-icons/bs";
import VideoPlayer from "../components/player/VideoPlayer";
import {
  useGetFilteredVideosQuery,
  useGetVideoByIdQuery,
} from "../features/api/videoApiSlice";
import PlansAndProducts from "../components/plansAndProducts/PlansAndProducts";
import LoadingSpinner from "../components/loader/Loader";
import { useGetMyListQuery } from "../features/api/myListApiSlice";
import useAuth from "../hooks/useAuth";
import { useVideo } from "../hooks/useVideo";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setHasInteracted } from "../components/slice/sliceSlice";
import PaginatedVideosGrid from "../features/video/PaginatedVideosGrid";
import FilterComponent from "../features/video/FilterVideo";

const WatchPage = () => {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isLoggedIn } = useAuth();

  const [mobileFilter, setMobileFilter] = useState(false);
  // This is for pagination persist in url e.g when in page 2 and refresh
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromQuery = parseInt(searchParams.get("page") || "1", 10);
  const videosPerPage = 9;
  const [currentPage, setCurrentPage] = useState(pageFromQuery);

  // 🔹 Extract filter state from URL
  const videoType = searchParams.get("videoType") || "";
  const category = searchParams.get("category") || "";
  const searchTerm = searchParams.get("searchTerm") || "";

  // 🔹 New handler for filter and pagination changes
  const handleFilterChange = (newFilterState) => {
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({
      ...currentParams,
      ...newFilterState,
      page: newFilterState.page || "1", // Reset to page 1 on filter change
    });
  };

  // 🔹 Keep state & URL in sync
  useEffect(() => {
    if (pageFromQuery !== currentPage) {
      setCurrentPage(pageFromQuery);
    }
  }, [pageFromQuery]);

  const { handleSelectedVideoToPlay, scrollY, persistScroll } = useVideo();

  const { data, isLoading } = useGetVideoByIdQuery(idOrSlug);
  const { data: listData } = useGetMyListQuery(undefined, {
    skip: !isLoggedIn,
  });

  useEffect(() => {
    if (!data?.video) return;
    handleSelectedVideoToPlay(data.video);
    dispatch(setHasInteracted(true));
    if (scrollY) {
      window.scrollTo({ top: scrollY });
    } else if (location.key === "default") {
      window.scrollTo({ top: 0 });
    }
  }, [
    idOrSlug,
    dispatch,
    data?.video,
    handleSelectedVideoToPlay,
    location,
    scrollY,
  ]);

  const handleNavigate = (nextVideo, videos) => {
    persistScroll();
    const params = new URLSearchParams(location.search);
    navigate(
      `/watch/${nextVideo?.slug || nextVideo?._id}?${params.toString()}`,
      {
        state: { source: location.state?.source, videos, autoPlayed: true },
      }
    );
  };

  const toggleMobileFilterMenu = () => setMobileFilter((prev) => !prev);

  const { video, relatedVideos, isSubscribed, hasElevatedAccess } = data || {};
  const myList = listData?.myList || [];
  const source = location.state?.source || "videoList";
  // 🔹 Sidebar logic
  const sidebarVideos =
    source === "myList" && isLoggedIn ? myList : relatedVideos;
  const sidebarTitle =
    source === "myList" && isLoggedIn ? "My List" : "Related Videos";

  // 🔹 RTK query for bottom videos
  const { data: paginatedData, isLoading: paginatedLoading } =
    useGetFilteredVideosQuery(
      {
        videoType: videoType,
        searchTerm: searchTerm,
        category: category,
        page: pageFromQuery, // Use pageFromQuery instead of currentPage
        limit: videosPerPage,
      },
      { skip: source !== "videoList" }
    );

  const {
    videos: allVideos = [],
    totalPages = 1,
    totalVideos = 0,
  } = paginatedData || {};

  const bottomVideos = source === "videoList" ? allVideos : relatedVideos;
  const bottomTitle = source === "videoList" ? "All Videos" : "Related Videos";

  if (isLoading || (source === "videoList" && paginatedLoading))
    return <LoadingSpinner />;

  return (
    <div className="main-area-flex">
      <div className="main-area-left">
        {/* 🔹 Conditionally render FilterComponent for 'All Videos' */}
        {source === "videoList" && (
          <FilterComponent
            videoType={videoType}
            category={category}
            searchTerm={searchTerm}
            handleFilterChange={handleFilterChange}
            mobileFilter={mobileFilter}
            toggleMobileFilterMenu={toggleMobileFilterMenu}
            videos={allVideos} // Pass down videos to filter categories
          />
        )}
      </div>

      {mobileFilter && (
        <div
          className="mobile-filter-overlay active"
          onClick={toggleMobileFilterMenu}
        ></div>
      )}
      {!mobileFilter && (
        <button
          className="floating-filter-btn"
          onClick={toggleMobileFilterMenu}
        >
          <BsFilterRight aria-hidden="true" />
          Filters
        </button>
      )}
      <div className="main-area-center">
        <VideoPlayer
          videos={bottomVideos?.length ? bottomVideos : [video]}
          isSubscribed={isSubscribed}
          hasElevatedAccess={hasElevatedAccess}
          video={video}
        />

        <div className="video-area">
          <div className="video-header">
            <h4 className="total-videos">Total Videos: {totalVideos}</h4>
          </div>

          <div className="related-videos">
            <PaginatedVideosGrid
              videos={bottomVideos}
              currentPage={pageFromQuery} // Use the URL search param directly
              totalPages={totalPages}
              activeVideoId={video?._id}
              source={source}
              navigate={navigate}
            />
          </div>
        </div>
      </div>
      <PlansAndProducts video={video} />
    </div>
  );
};

export default WatchPage;
